import React from 'react';
import { Container } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { HashLink } from 'react-router-hash-link';
import { ArrowRight } from 'react-bootstrap-icons';
import { Banner } from '../Banner';
import atlassian from '../../Assets/Images/companies/atlassian.svg';
import otto from '../../Assets/Images/companies/otto.png';
import ottoFactory from '../../Assets/Images/otto-factory.webp';
import { projects } from '../../data/projects';
import './Home.css';

const processMonitor = projects.find(project => project.id === 'process-monitor');

export const Home = () => (
  <>
    <Banner />
    <section className="home-highlights" id="highlights" aria-labelledby="highlights-heading">
      <Container>
        <header className="work-section-heading">
          <h2 id="highlights-heading">Some of my work<span className="accent">.</span></h2>
          <p>A few examples from my co-ops, research, and personal projects.</p>
        </header>

        <div className="work-stories">
          <article className="work-story" aria-labelledby="atlassian-highlight-heading">
            <div className="work-story-copy">
              <div className="work-identity">
                <span className="work-logo work-logo-atlassian"><img src={atlassian} alt="" width="28" height="28" /></span>
                <div><p className="work-company">Atlassian</p><p className="work-context">Software Engineering Intern · Summer 2026</p></div>
              </div>
              <h3 id="atlassian-highlight-heading">Resource attribution for Rovo’s third-party ingestion.</h3>
              <p>I built a Kotlin attribution layer for Rovo’s third-party data ingestion, tracking resource usage by connector. The telemetry also identified an abandoned Asana workflow left enabled for over 14 months. Turning it off eliminated about $160/day in wasted staging spend.</p>
              <HashLink className="text-link work-story-link" to="/career#atlassian">Explore my work at Atlassian <ArrowRight aria-hidden="true" /></HashLink>
            </div>
            <div className="work-atlassian-results">
              <p className="work-result-label">Wasted staging spend eliminated</p>
              <p className="work-cost-result"><span className="work-cost-approx">≈</span>$160<span>/day</span></p>
              <figure className="work-latency">
                <figcaption>Resource attribution instrumentation <span>p50 latency (median)</span></figcaption>
                <div className="work-latency-row"><span>Before</span><div className="work-latency-track" aria-hidden="true"><span className="work-latency-bar work-latency-before" /></div><strong>8 ms</strong></div>
                <div className="work-latency-row"><span>After</span><div className="work-latency-track" aria-hidden="true"><span className="work-latency-bar work-latency-after" /></div><strong>1 ms</strong></div>
              </figure>
            </div>
          </article>

          <article className="work-story" aria-labelledby="otto-highlight-heading">
            <div className="work-story-copy">
              <div className="work-identity">
                <span className="work-logo work-logo-otto"><img src={otto} alt="" width="40" height="40" /></span>
                <div><p className="work-company">OTTO by Rockwell Automation</p><p className="work-context">Software Developer Intern · Winter 2025</p></div>
              </div>
              <h3 id="otto-highlight-heading">Multi-fleet interoperability.</h3>
              <p>I worked on fleet coordination for <strong>500+ robots</strong> using C++, Python, and ROS2. I also built a proof of concept for migrating networking from CycloneDDS to Zenoh, achieving <strong>25% lower latency</strong> and <strong>30% higher throughput</strong>.</p>
              <HashLink className="text-link work-story-link" to="/career#otto">Explore my work at OTTO <ArrowRight aria-hidden="true" /></HashLink>
            </div>
            <figure className="work-robot-photo">
              <div className="work-robot-frame"><img src={ottoFactory} alt="An OTTO 1500 mobile robot carrying a load on a factory floor." width="1200" height="800" loading="lazy" /></div>
              <figcaption>OTTO 1500 · <a href="https://ottomotors.com/press-kit/">Photo: OTTO</a></figcaption>
            </figure>
          </article>
        </div>

        <div className="work-explorations">
          <article className="work-exploration" aria-labelledby="project-highlight-heading">
            <div className="work-exploration-copy">
              <p className="work-context">Personal project · C++ / macOS</p>
              <h3 id="project-highlight-heading">Process Monitor</h3>
              <p>I built a C++ terminal monitor for macOS. It displays CPU and memory usage, with keyboard controls to sort processes by usage or group them by name.</p>
            </div>
            <HashLink className="work-terminal-preview" to="/projects#process-monitor" aria-label="Explore Process Monitor"><img src={processMonitor.image} alt="Process Monitor displaying CPU and memory usage in a terminal." width="600" height="360" loading="lazy" /></HashLink>
            <HashLink className="text-link work-story-link" to="/projects#process-monitor">See how I built it <ArrowRight aria-hidden="true" /></HashLink>
          </article>
          <article className="work-exploration" aria-labelledby="research-highlight-heading">
            <div className="work-exploration-copy">
              <p className="work-context">Undergraduate research · University of Waterloo</p>
              <h3 id="research-highlight-heading">Oblivious vector search.</h3>
              <p>I built a C++ prototype of oblivious HNSW vector search and benchmarked it in trusted execution environments, measuring performance and access-pattern leakage.</p>
            </div>
            <div className="work-research-notes">
              <p>What I measured</p>
              <ul><li>Latency</li><li>Memory overhead</li><li>Graph-traversal leakage</li></ul>
            </div>
            <HashLink className="text-link work-story-link" to="/career#waterloo-research">Explore my privacy research <ArrowRight aria-hidden="true" /></HashLink>
          </article>
        </div>

        <nav className="work-more" aria-label="More of my work">
          <p>More co-op experience and personal projects.</p>
          <div><Link className="text-link" to="/projects">More of my projects <ArrowRight aria-hidden="true" /></Link><Link className="text-link" to="/career">My experience <ArrowRight aria-hidden="true" /></Link></div>
        </nav>
      </Container>
    </section>
  </>
);
